import { useNavigate } from "react-router-dom";
import { Receipt, Sparkle, Checks, Export, DeviceMobileCamera, ShieldCheck, UsersThree, Check, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useT, LangToggle } from "../i18n";

const FEATURES = [
  { icon: Sparkle, k: "f1" },
  { icon: Checks, k: "f2" },
  { icon: Export, k: "f3" },
  { icon: DeviceMobileCamera, k: "f4" },
  { icon: UsersThree, k: "f5" },
  { icon: ShieldCheck, k: "f6" },
];

const PLANS = [
  { name: "Freelancer", price: "$0", curr: "", periodKey: "forever", descKey: "plan_fl_desc", featuresKey: "plan_fl_features", ctaKey: "cta_free", highlight: false },
  { name: "Team", price: "$7.900", curr: "CLP", periodKey: "perUserMonth", descKey: "plan_team_desc", featuresKey: "plan_team_features", ctaKey: "cta_trial", highlight: true },
  { name: "Business", price: "$14.900", curr: "CLP", periodKey: "perUserMonth", descKey: "plan_biz_desc", featuresKey: "plan_biz_features", ctaKey: "cta_sales", highlight: false },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const go = () => navigate("/login");

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 sticky top-0 bg-white/90 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-900">
            <Receipt size={24} weight="bold" />
            <span className="font-heading font-bold tracking-tight">Pellku</span>
          </div>
          <nav className="hidden sm:flex items-center gap-8 text-sm text-slate-500">
            <a href="#features" className="hover:text-slate-900 transition-colors duration-150" data-testid="nav-features-link">{t("landing_features")}</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors duration-150" data-testid="nav-pricing-link">{t("landing_pricing")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Button data-testid="landing-signin-btn" onClick={go} variant="ghost" className="rounded-none text-sm">{t("landing_signin")}</Button>
            <Button data-testid="landing-get-started-btn" onClick={go} className="rounded-none bg-slate-900 hover:bg-slate-700 text-sm">{t("landing_getstarted")}</Button>
          </div>
        </div>
      </header>

      <section className="bg-pro border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
        <div className="max-w-3xl">
          <p className="font-mono text-xs text-slate-400 uppercase tracking-[0.25em] mb-6">{t("hero_kicker")}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            {t("hero_title1")}<br />{t("hero_title2")}
          </h1>
          <p className="text-base md:text-lg text-slate-500 mt-6 max-w-xl leading-relaxed">
            {t("hero_sub")}
          </p>
          <div className="flex items-center gap-4 mt-10">
            <Button data-testid="hero-cta-btn" onClick={go} className="rounded-none bg-slate-900 hover:bg-slate-700 h-12 px-7 text-base">
              {t("hero_cta")} <ArrowRight size={18} className="ml-2" />
            </Button>
            <a href="#pricing" data-testid="hero-pricing-link" className="text-sm font-semibold text-slate-900 underline underline-offset-4 hover:text-slate-600 transition-colors duration-150">{t("hero_seepricing")}</a>
          </div>
          <div className="font-mono text-xs text-slate-400 mt-12 flex flex-wrap gap-x-8 gap-y-2 border-t border-slate-200/70 pt-6">
            <span>{t("hero_line1")}</span>
            <span>{t("hero_line2")}</span>
            <span>{t("hero_line3")}</span>
          </div>
        </div>
        </div>
      </section>

      <section id="features" className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
          <p className="font-mono text-xs text-slate-400 uppercase tracking-[0.25em] mb-3">{t("feat_kicker")}</p>
          <h2 className="font-heading text-lg font-bold text-slate-900 tracking-tight mb-12">{t("feat_title")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200 bg-white">
            {FEATURES.map(({ icon: Icon, k }) => (
              <div key={k} className="border-b border-r border-slate-200 p-7 hover:bg-slate-50 transition-colors duration-150">
                <Icon size={24} className="text-slate-900 mb-4" />
                <p className="font-heading font-semibold text-slate-900">{t(`${k}t`)}</p>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t(`${k}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
          <p className="font-mono text-xs text-slate-400 uppercase tracking-[0.25em] mb-3">{t("pricing_kicker")}</p>
          <h2 className="font-heading text-lg font-bold text-slate-900 tracking-tight mb-12">{t("pricing_title")}</h2>
          <div className="grid md:grid-cols-3 gap-0 border border-slate-200 divide-y md:divide-y-0 md:divide-x divide-slate-200" data-testid="pricing-grid">
            {PLANS.map((p) => (
              <div key={p.name} data-testid={`pricing-plan-${p.name.toLowerCase()}`} className={`p-8 flex flex-col ${p.highlight ? "bg-slate-900 text-white" : "bg-white"}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">{p.name}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-mono text-4xl font-semibold">{p.price}</span>
                  {p.curr && <span className="font-mono text-xs font-semibold text-slate-400">{p.curr}</span>}
                  <span className="text-xs text-slate-400">{t(p.periodKey)}</span>
                </div>
                <p className={`text-sm mt-3 ${p.highlight ? "text-slate-300" : "text-slate-500"}`}>{t(p.descKey)}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {t(p.featuresKey).map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-slate-200" : "text-slate-600"}`}>
                      <Check size={15} className={`mt-0.5 shrink-0 ${p.highlight ? "text-emerald-400" : "text-emerald-600"}`} /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  onClick={go}
                  className={`rounded-none w-full mt-8 h-11 ${p.highlight ? "bg-white text-slate-900 hover:bg-slate-200" : "bg-slate-900 text-white hover:bg-slate-700"}`}
                >
                  {t(p.ctaKey)}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-6 font-mono">{t("pricing_note")}</p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-900">
            <Receipt size={18} weight="bold" />
            <span className="font-heading font-bold text-sm tracking-tight">Pellku</span>
          </div>
          <p className="font-mono text-xs text-slate-400">{t("footer_tag")}</p>
        </div>
      </footer>
    </div>
  );
}
